import type { BootstrapSummary } from '@core/entities/BootstrapSummary';
import { BootstrapGatewayError, type BootstrapGateway } from '@core/ports/BootstrapGateway';
import { AuthenticatedBffTransport } from '@infrastructure/http/AuthenticatedBffTransport';
import { BffTransportError } from '@infrastructure/http/BffTransportError';
import { hasTransientRetryStop } from '@infrastructure/http/retryOutcomeHeaders';

export class BffBootstrapHttpGateway implements BootstrapGateway {
  private static readonly offlineMessage = 'Your internet connection appears to be unavailable. Reconnect to the internet, then start again from the NHS App if you still need help.';

  private static readonly sessionRequiredMessage = 'Start this journey from the NHS App to continue.';

  public constructor(private readonly transport = new AuthenticatedBffTransport()) {}

  public async getBootstrapSummary(): Promise<BootstrapSummary> {
    const response = await this.fetchResponse('/api/bootstrap/summary');
    return this.readBootstrapSummary(response);
  }

  public async confirmJourneyStartAvailability(): Promise<void> {
    const response = await this.fetchResponse('/api/triage/status');
    this.ensureJourneyStartAvailable(response);
  }

  private async fetchResponse(path: string): Promise<Response> {
    let response: Response;

    try {
      response = await this.transport.fetch(path, {
        headers: {
          Accept: 'application/json'
        }
      });
    } catch (error) {
      if (error instanceof BffTransportError && error.code === 'offline') {
        throw new BootstrapGatewayError('offline', BffBootstrapHttpGateway.offlineMessage);
      }

      throw error;
    }

    if (response.status === 401) {
      throw new BootstrapGatewayError('session-required', BffBootstrapHttpGateway.sessionRequiredMessage);
    }

    return response;
  }

  private async readBootstrapSummary(response: Response): Promise<BootstrapSummary> {
    if (!response.ok) {
      if (hasTransientRetryStop(response.headers)) {
        throw new BootstrapGatewayError('retry-exhausted', 'Intelligent Navigator could not be reached right now.');
      }

      throw new BootstrapGatewayError('request-failed', `Bootstrap summary request failed with status ${response.status}.`);
    }

    return (await response.json()) as BootstrapSummary;
  }

  private ensureJourneyStartAvailable(response: Response): void {
    if (response.ok) {
      return;
    }

    if (hasTransientRetryStop(response.headers)) {
      throw new BootstrapGatewayError('retry-exhausted', 'Intelligent Navigator could not be reached right now.');
    }

    throw new BootstrapGatewayError('request-failed', `Journey start handoff request failed with status ${response.status}.`);
  }
}
